import { action, computed, makeObservable, observable } from 'mobx';

export const MIN_TERMINAL_HEIGHT = 160;
export const MAX_TERMINAL_HEIGHT = 1200;
const HEIGHT_STORAGE_KEY = 'terminal-panel-height';
const TABS_STORAGE_KEY = 'terminal-local-tabs';

const clampHeight = (height: number): number =>
  Math.min(
    MAX_TERMINAL_HEIGHT,
    Math.max(MIN_TERMINAL_HEIGHT, Math.round(height))
  );

function loadHeight(): number {
  if (typeof window === 'undefined') return 400;
  const stored = Number(localStorage.getItem(HEIGHT_STORAGE_KEY));
  if (Number.isFinite(stored) && stored > 0) return clampHeight(stored);
  return clampHeight(window.innerHeight * 0.45);
}

/** `sandbox` = the chat's E2B sandbox (internal); `local` = a shell on this machine. */
export type TerminalKind = 'sandbox' | 'local';

/**
 * Why the active local tab should (or should not) take keyboard focus.
 *
 * - `user`: the user asked for this terminal (ctrl+`, new tab, tab switch).
 *   Focus it, even if that means leaving the chat composer.
 * - `soft`: the user did not ask for *this* terminal but was working in the
 *   dock (closed the tab they were typing in). Focus the neighbour unless focus
 *   has meanwhile gone to an editable outside the dock.
 * - `null`: nothing pending. Agent-run tabs (adoptSession) leave it null so a
 *   command popping in never yanks the cursor out of the composer.
 */
export type TerminalFocusIntent = 'user' | 'soft' | null;

/**
 * One local terminal tab. `key` is the renderer's identity for the mounted
 * component (stable for the tab's life); `sessionId` is the host's identity for
 * the shell, null until the host acks the first connection.
 */
export interface LocalTab {
  key: string;
  sessionId: string | null;
  /** Host-reported OSC 0/2 title; empty until the shell sets one. */
  title: string;
  /**
   * Set when the tab was opened for an agent-run command (`TERMINAL_RUN`): the
   * command line. Such a tab stays open after the command exits so the output can
   * be read; `exited` marks that state.
   */
  command: string | null;
  exited: boolean;
}

interface PersistedTabs {
  tabs: LocalTab[];
  activeKey: string | null;
}

function loadTabs(): PersistedTabs {
  if (typeof window === 'undefined') return { tabs: [], activeKey: null };
  try {
    const raw = sessionStorage.getItem(TABS_STORAGE_KEY);
    if (!raw) return { tabs: [], activeKey: null };
    const parsed = JSON.parse(raw) as Partial<PersistedTabs>;
    const tabs: LocalTab[] = Array.isArray(parsed.tabs)
      ? parsed.tabs
          .filter(
            (t) => typeof t?.key === 'string' && typeof t.sessionId === 'string'
          )
          .map((t) => ({
            key: t.key,
            sessionId: t.sessionId,
            title: typeof t.title === 'string' ? t.title : '',
            command: typeof t.command === 'string' ? t.command : null,
            // a remembered run tab whose session is gone is dropped by reconcile()
            exited: false,
          }))
      : [];
    const activeKey =
      typeof parsed.activeKey === 'string' &&
      tabs.some((t) => t.key === parsed.activeKey)
        ? parsed.activeKey
        : (tabs[0]?.key ?? null);
    return { tabs, activeKey };
  } catch {
    return { tabs: [], activeKey: null };
  }
}

/** Focus is in a text field / contenteditable that is not part of the terminal dock. */
function isEditableOutsideDockFocused(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement;
  if (!el || el === document.body) return false;
  if (el.closest('[data-testid="local-terminal-panel"]')) return false;
  const tag = el.tagName;
  return (
    tag === 'TEXTAREA' ||
    tag === 'INPUT' ||
    (el as HTMLElement).isContentEditable
  );
}

const newKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

/**
 * Bottom-docked terminal panel state. One dock, two backends (see TerminalKind).
 * The sandbox terminal is bound to the chat whose sandbox it targets and renders in
 * the chat layout; the local terminal is app-wide (chatId null) and renders in
 * MainLayout so it is reachable from Home and survives chat switches.
 *
 * Local terminal = tabs. Every tab stays mounted while it exists (hidden tabs are
 * display:none, still attached to their shell) so switching is instant and nothing
 * is replayed. `isLocalOpen` is dock visibility; tabs live independently of it, so
 * ctrl+` hides and shows without touching any shell. Tabs persist to
 * sessionStorage (host session ids) so a renderer reload reattaches to the same
 * shells; `reconcile()` drops any the host no longer has.
 *
 * Height is user-resizable (top-edge drag) and persisted per device so the dock
 * comes back at the last size across restarts.
 */
class SandboxTerminalPanelStore {
  isOpen = false;
  kind: TerminalKind = 'sandbox';
  /** Bound chat for the sandbox kind; always null for local. */
  chatId: string | null = null;
  tabs: LocalTab[];
  activeKey: string | null;
  height = loadHeight();
  /**
   * Pending focus request for the active tab; see TerminalFocusIntent. Stays
   * set until a mounted terminal actually consumes it, so an intent raised
   * before the tab's xterm exists (first open, new tab) is honoured once it
   * registers rather than lost to the mount race.
   */
  focusIntent: TerminalFocusIntent = 'user';

  /** Focus handlers registered by mounted local terminals, by tab key. */
  private localFocusHandlers = new Map<string, () => void>();
  /** Kill handlers (send `kill` to the host) registered by mounted terminals, by tab key. */
  private localKillHandlers = new Map<string, () => boolean>();

  constructor() {
    const persisted = loadTabs();
    this.tabs = persisted.tabs;
    this.activeKey = persisted.activeKey;
    makeObservable<this, 'persistTabs'>(this, {
      isOpen: observable,
      kind: observable,
      chatId: observable,
      tabs: observable.shallow,
      activeKey: observable,
      focusIntent: observable,
      consumeFocusIntent: action,
      height: observable,
      isLocalOpen: computed,
      localMounted: computed,
      activeTab: computed,
      open: action,
      openLocal: action,
      hideLocal: action,
      newTab: action,
      adoptSession: action,
      markExited: action,
      activateTab: action,
      activateAdjacentTab: action,
      activateTabAt: action,
      removeTab: action,
      setTabSession: action,
      setTabTitle: action,
      reconcile: action,
      close: action,
      toggle: action,
      toggleLocal: action,
      setHeight: action,
      persistTabs: action,
    });
  }

  // ---- sandbox ---------------------------------------------------------------

  open(chatId: string): void {
    this.isOpen = true;
    this.kind = 'sandbox';
    this.chatId = chatId;
  }

  toggle(chatId: string): void {
    if (this.isSandboxOpenFor(chatId)) {
      this.close();
    } else {
      this.open(chatId);
    }
  }

  isSandboxOpenFor(chatId: string): boolean {
    return this.isOpen && this.kind === 'sandbox' && this.chatId === chatId;
  }

  // ---- local ------------------------------------------------------------------

  get isLocalOpen(): boolean {
    return this.isOpen && this.kind === 'local';
  }

  /** At least one local shell exists (possibly hidden). */
  get localMounted(): boolean {
    return this.tabs.length > 0;
  }

  get activeTab(): LocalTab | null {
    return this.tabs.find((t) => t.key === this.activeKey) ?? null;
  }

  /** Show the local terminal, creating a first tab if there is none. */
  openLocal(): void {
    this.isOpen = true;
    this.kind = 'local';
    this.chatId = null;
    if (this.tabs.length === 0) {
      this.newTab();
    } else {
      this.focusIntent = 'user';
    }
  }

  /** Hide the dock but keep every shell running. */
  hideLocal(): void {
    if (this.isLocalOpen) this.isOpen = false;
  }

  toggleLocal(): void {
    if (this.isLocalOpen) {
      this.hideLocal();
    } else {
      this.openLocal();
    }
  }

  /** Add a tab (fresh shell) and make it active. */
  newTab(): LocalTab {
    const tab: LocalTab = {
      key: newKey(),
      sessionId: null,
      title: '',
      command: null,
      exited: false,
    };
    this.tabs = [...this.tabs, tab];
    this.activeKey = tab.key;
    this.focusIntent = 'user';
    this.persistTabs();
    return tab;
  }

  /**
   * Attach a tab to a session the host already has (an agent-run command). Shows
   * the dock and activates the tab so the user watches it live. No-op if a tab is
   * already on that session.
   */
  adoptSession(sessionId: string, command: string): LocalTab {
    const existing = this.tabs.find((t) => t.sessionId === sessionId);
    if (existing) {
      this.isOpen = true;
      this.kind = 'local';
      this.chatId = null;
      this.activeKey = existing.key;
      this.focusIntent = null;
      return existing;
    }
    const tab: LocalTab = {
      key: newKey(),
      sessionId,
      title: command,
      command,
      exited: false,
    };
    this.tabs = [...this.tabs, tab];
    this.activeKey = tab.key;
    this.isOpen = true;
    this.kind = 'local';
    this.chatId = null;
    this.focusIntent = null;
    this.persistTabs();
    return tab;
  }

  /** The command behind a run tab has finished; the tab stays until closed. */
  markExited(key: string): void {
    const tab = this.tabs.find((t) => t.key === key);
    if (!tab || tab.exited) return;
    this.replaceTab(key, { ...tab, exited: true });
    // nothing to reattach to after a reload
    this.persistTabs();
  }

  activateTab(key: string): void {
    if (this.activeKey === key || !this.tabs.some((t) => t.key === key)) return;
    this.activeKey = key;
    this.focusIntent = 'user';
    this.persistTabs();
  }

  activateAdjacentTab(direction: 1 | -1): void {
    const n = this.tabs.length;
    if (n < 2) return;
    const index = this.tabs.findIndex((t) => t.key === this.activeKey);
    const next = ((((index < 0 ? 0 : index) + direction) % n) + n) % n;
    this.activateTab(this.tabs[next].key);
  }

  /** 1-based, like cmd+1..9 in every terminal. Out of range is a no-op. */
  activateTabAt(position: number): void {
    const tab = this.tabs[position - 1];
    if (tab) this.activateTab(tab.key);
  }

  /**
   * The shell behind this tab is gone (killed via X, exited, connection lost).
   * Activates a neighbour; hides the dock when the last tab goes.
   */
  removeTab(key: string): void {
    const index = this.tabs.findIndex((t) => t.key === key);
    if (index < 0) return;
    this.tabs = this.tabs.filter((t) => t.key !== key);
    this.localFocusHandlers.delete(key);
    this.localKillHandlers.delete(key);
    if (this.activeKey === key) {
      const neighbour = this.tabs[Math.min(index, this.tabs.length - 1)];
      this.activeKey = neighbour?.key ?? null;
      // The closed tab's xterm is unmounting and takes focus to body with it;
      // hand it to the neighbour unless the user is already typing elsewhere.
      if (neighbour) this.focusIntent = 'soft';
    }
    if (this.tabs.length === 0 && this.kind === 'local') this.isOpen = false;
    this.persistTabs();
  }

  setTabSession(key: string, sessionId: string): void {
    const tab = this.tabs.find((t) => t.key === key);
    if (!tab || tab.sessionId === sessionId) return;
    this.replaceTab(key, { ...tab, sessionId });
    this.persistTabs();
  }

  setTabTitle(key: string, title: string): void {
    const tab = this.tabs.find((t) => t.key === key);
    if (!tab || tab.title === title) return;
    this.replaceTab(key, { ...tab, title });
    this.persistTabs();
  }

  /**
   * `tabs` is `observable.shallow`, so mutating a tab in place is invisible to
   * observers (the computed `activeTab`, the panel's active session id). Swap
   * the element instead so the array itself changes.
   */
  private replaceTab(key: string, next: LocalTab): void {
    const index = this.tabs.findIndex((t) => t.key === key);
    if (index === -1) return;
    this.tabs[index] = next;
  }

  /**
   * Drop remembered tabs whose sessions the host no longer has (host restarted with
   * main; reaped while the app was closed). Tabs without a session id yet are kept.
   */
  reconcile(liveSessionIds: Iterable<string>): void {
    const live = new Set(liveSessionIds);
    const kept = this.tabs.filter(
      (t) => t.sessionId === null || live.has(t.sessionId)
    );
    if (kept.length === this.tabs.length) return;
    this.tabs = kept;
    if (!kept.some((t) => t.key === this.activeKey)) {
      this.activeKey = kept[0]?.key ?? null;
    }
    if (kept.length === 0 && this.kind === 'local') this.isOpen = false;
    this.persistTabs();
  }

  /**
   * Returns an unregister function; the terminal calls it on unmount. If this
   * is the active tab and a focus intent is pending (raised before the xterm
   * existed), it is consumed here - that is what makes first-open and new-tab
   * land in the shell.
   */
  registerLocalFocus(key: string, focus: () => void): () => void {
    this.localFocusHandlers.set(key, focus);
    if (key === this.activeKey && this.isLocalOpen && this.focusIntent) {
      this.consumeFocusIntent();
    }
    return () => {
      if (this.localFocusHandlers.get(key) === focus) {
        this.localFocusHandlers.delete(key);
      }
    };
  }

  /** Move keyboard focus into the active local tab. True if a terminal took it. */
  focusLocal(): boolean {
    if (!this.activeKey) return false;
    const focus = this.localFocusHandlers.get(this.activeKey);
    if (!focus) return false;
    focus();
    return true;
  }

  /**
   * Apply the pending focus intent to the active tab if its terminal is mounted.
   * `soft` yields to an editable outside the dock that already has focus (the
   * user moved on); `user` does not. Clears the intent only when honoured or
   * yielded, never when the terminal simply is not mounted yet.
   */
  consumeFocusIntent(): void {
    const intent = this.focusIntent;
    if (!intent || !this.activeKey) return;
    if (!this.localFocusHandlers.has(this.activeKey)) return;
    if (intent === 'soft' && isEditableOutsideDockFocused()) {
      this.focusIntent = null;
      return;
    }
    this.focusIntent = null;
    this.focusLocal();
  }

  registerLocalKill(key: string, kill: () => boolean): () => void {
    this.localKillHandlers.set(key, kill);
    return () => {
      if (this.localKillHandlers.get(key) === kill) {
        this.localKillHandlers.delete(key);
      }
    };
  }

  /**
   * Ask the host to end this tab's shell. The terminal removes the tab when the
   * host confirms (exit + close). If no terminal is mounted for the key (never
   * connected), the tab is removed directly.
   */
  requestKill(key: string): void {
    const tab = this.tabs.find((t) => t.key === key);
    const kill = this.localKillHandlers.get(key);
    // Through the socket when there is one: the host replies exit + close,
    // which routes to onClose and removes the tab.
    if (kill && !tab?.exited && kill()) return;
    // No live socket (view crashed before connecting, socket dropped): ask
    // main to kill the shell so closing the tab never leaves one running.
    if (tab?.sessionId && !tab.exited) this.sessionKiller?.(tab.sessionId);
    this.removeTab(key);
  }

  /** Out-of-band kill, set by the panel (the store stays free of electron imports). */
  private sessionKiller: ((sessionId: string) => void) | null = null;
  setSessionKiller(fn: ((sessionId: string) => void) | null): void {
    this.sessionKiller = fn;
  }

  // ---- shared -----------------------------------------------------------------

  /** Hide whatever the dock is showing. Does not kill any local shell. */
  close(): void {
    this.isOpen = false;
    this.chatId = null;
  }

  setHeight(height: number): void {
    this.height = clampHeight(height);
    if (typeof window !== 'undefined') {
      localStorage.setItem(HEIGHT_STORAGE_KEY, String(this.height));
    }
  }

  private persistTabs(): void {
    if (typeof window === 'undefined') return;
    // only tabs the host knows about (and still has) are worth remembering
    const tabs = this.tabs
      .filter((t) => t.sessionId !== null && !t.exited)
      // loadTabs() rebuilds every persisted tab as not-exited, so write that
      .map(({ key, sessionId, title, command }) => ({
        key,
        sessionId,
        title,
        command,
        exited: false,
      }));
    const data: PersistedTabs = { tabs, activeKey: this.activeKey };
    sessionStorage.setItem(TABS_STORAGE_KEY, JSON.stringify(data));
  }
}

export const sandboxTerminalPanelStore = new SandboxTerminalPanelStore();
